import type { ControlFormProps } from '@ingenico/react-section-utils';
import { has } from 'lodash-es';
import type { FieldPath } from 'react-hook-form';
import type { ApplicationVersionDefinitionWithOptions } from '../../components/os-version-definition-step/osVersionDefinitionStep.model';
import type { ArtifactComponentTypeEnum, ArtifactResource } from '../../domain/models/artifact.model';
import type { TemplateResponseResourceFront } from '../../domain/models/template.model';
import type { UseNewDeviceAppVersionFieldsDependenciesReturn } from '../../hooks/useNewOsVersionFieldsDependencies.hook';

export interface OsVersionDefinition {
  readonly version: string;
  readonly releaseNotes: string;
}

export interface OsVersionBuild {
  readonly applicationFiles: ParameterTemplate[];
}

export interface OsAppVersion {
  readonly definition: OsVersionDefinition;
  readonly build: OsVersionBuild;
}

export enum OsVersionStepsKeys {
  Definition = 'Definition',
  Build = 'Build',
  Summary = 'Summary',
}

export interface OsVersionAutocompleteFieldValue {
  readonly value: string;
  readonly label: string;
  readonly targetLocation?: string;
}

export interface OsVersionDefinitionWithOptions {
  readonly hiddenFields?: FieldPath<OsAppVersion>[];
  readonly applicationFiles: UseNewDeviceAppVersionFieldsDependenciesReturn['applicationFiles'];
  readonly fieldsLoading?: Partial<Record<string, boolean>>;
}

export interface OsVersionDefinitionStepProps {
  readonly control: ControlFormProps<OsAppVersion>['control'];
  readonly definitionOptions?: ApplicationVersionDefinitionWithOptions;
}

export enum OSArtifactType {
  Template = 'TEMPLATE',
  SwPackage = 'SW_PACKAGE',
  DataFile = 'DATA_FILE',
}

export interface GenericArtifact {
  readonly artifactName: string;
  readonly checksum: string;
  readonly type: OSArtifactType;
  readonly file: Blob;
  readonly id: string;
  readonly componentType: ArtifactComponentTypeEnum;
}

export interface GenericArtifactForUpload extends GenericArtifact {
  readonly templateTargetLocation?: string;
}

export interface OsVersionParams {
  readonly redirectRouteOnCreate?: string;
  readonly parentEntityId: string;
}

export interface AppVersionApplicationFile {
  readonly checksum: string;
  readonly componentType: Extract<ArtifactComponentTypeEnum, 'APPLICATION' | 'OS' | 'SYSTEM_PACKAGE'>;
  readonly error?: boolean;
  readonly extension?: string;
  readonly file?: File;
  readonly id?: string;
  readonly messageError?: string;
  readonly name: string;
  readonly order?: number;
  readonly parsedTemplateId?: string;
  readonly size: number;
  readonly targetLocation?: string;
  readonly templateFileName?: string;
  readonly templateFileVersion?: string;
  readonly type?: OSArtifactType;
  readonly uploadId?: string;
  readonly uploadStatus?: AppVersionFileUploadErrorStatus;
}

//======== build
export interface ParameterTemplate {
  readonly targetLocation: string;
  readonly template: AppVersionApplicationFile[];
  readonly id: string;
}

export interface OnPartialUploadParams {
  readonly fulfilled: Record<string, AppVersionApplicationFile>;
  readonly rejected: Record<string, AppVersionApplicationFile>;
}

export enum AppVersionFileUploadError {
  UploadFileError = 'UPLOAD_FILE_ERROR',
}

export enum AppVersionFileUploadErrorStatus {
  None = 'NONE',
  Failure = 'FAILURE',
  Success = 'SUCCESS',
}

export type UploadedFileResponseInfo = {
  readonly targetLocation?: string;
  readonly uploadId?: string;
} & (ArtifactResource | TemplateResponseResourceFront);

export interface TargetLocationError {
  readonly targetLocation?: { readonly message?: string };
}

//======== artifacts
export function isParameterTemplate(value: AppVersionApplicationFile | ParameterTemplate): value is ParameterTemplate {
  return has(value, 'template') && has(value, 'targetLocation');
}
export function isArrayOfParameterTemplate(
  value: AppVersionApplicationFile[] | ParameterTemplate[],
): value is ParameterTemplate[] {
  return value.every((el) => isParameterTemplate(el));
}
